import { useCallback, useMemo, useState } from "react";

import { EmployeeAsyncActions } from "@/Common/Api/AsyncActions/EmployeeAsyncActions";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Locale } from "@/Common/Models/Localization/Locale";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalNoteViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalNote";
import AIScribeAction from "../Api/AIScribeAction";
import useAIScribeContentState from "../Hooks/useAIScribeContentState";
import { useAIScribeStartConsultation } from "../Hooks/useAIScribeStartConsultation";
import useUnableToSaveMedicalNoteWarningPopupState from "../Hooks/useUnableToSaveMedicalNoteWarningPopupState";
import { AIScribeConsultationNoteModel } from "../Models/AIScribeConsultationNoteModel";
import { IAIScribeContainerModel } from "../Models/IAIScribeContainerModel";
import { AIScribeCloseWarningPopup } from "./AIScribeCloseWarningPopup";
import { AIScribeLinkPopup } from "./AIScribeLinkPopup";
import { AIScribeMarketingPopup } from "./AIScribeMarketingPopup";
import { AIScribeMedicalNoteApprovedPopup } from "./AIScribeMedicalNoteApprovedPopup";
import { AIScribeMedicalNoteDeletedPopup } from "./AIScribeMedicalNoteDeletedPopup";
import { AIScribePopup } from "./AIScribePopup";
import { AIScribeSubscriptionExpiredPopup } from "./AIScribeSubscriptionExpiredPopup";
import { AIScribeUnlinkCarewayPopup } from "./AIScribeUnlinkCarewayPopup";
import { AIScribeWarningActiveAssistant } from "./AIScribeWarningActiveAssistant";

type cachedEmployeeEmail = {
    address: string | null;
    savedDateInMilliseconds: number | null;
};

interface AIScribeContainerProps {
    model: IAIScribeContainerModel;
}

function getEncounterModelIfUserIsCurrentlyEditing(patientContactId: string, encounterId: string): Encounter {
    const encounter = patientStore.PatientMedicalNoteModels[patientContactId]?.get("Encounter");
    if (sessionStore.activeMYLEMenuTab === MyleTabKey.Patients
        && patientContactId === patientStore.getCurrentPatientContactId()
        && patientStore.PatientUIModels[patientContactId]?.get("ActiveVerticalTab") === PatientTab.MedicalNote
        && patientStore.PatientMedicalNoteModels[patientContactId]?.get("ViewMode") === MedicalNoteViewMode.Edit
        && encounter?.Id === encounterId
    ) {
        return encounter;
    }

    return null;
}

function appendNewLine(...toAppend: string[]): string {
    return toAppend.filter(p => p).join("\n");
}

function appendGeneratedNoteToMedicalNote(
    encounter: Encounter,
    aiScribeConsultationNote: AIScribeConsultationNoteModel
): Encounter {
    if (!encounter)
        return null;

    return {
        Id: encounter.Id,
        VisitPurpose: appendNewLine(
            encounter.VisitPurpose,
            aiScribeConsultationNote.getReason()
        ),
        ExamenNote: appendNewLine(
            encounter.ExamenNote,
            aiScribeConsultationNote.getProgressNote()?.getExam()
        ),
        OtherPlanNote: appendNewLine(
            encounter.OtherPlanNote,
            aiScribeConsultationNote.getProgressNote()?.getFollowupAndPlan(),
            aiScribeConsultationNote.getPatientConsent() ? ("\n" + aiScribeConsultationNote.getPatientConsent()) : ""   // Patient consent is optional, and do not append if not given to avoid new lines.
        ),
        HistoricalNote: appendNewLine(
            encounter.HistoricalNote,
            aiScribeConsultationNote.getProgressNote()?.getHistoryOfIllness()
        ),
        ProgressNote: appendNewLine(
            encounter.ProgressNote,
            aiScribeConsultationNote.getProgressNote()?.getOther(),
            aiScribeConsultationNote.getUnknownNote()
        )
    } as Encounter;
}

function AIScribeContainer(props: Readonly<AIScribeContainerProps>): JSX.Element {
    const action = useMemo(() => new AIScribeAction(), []);
    const employeeAction = useMemo(() => new EmployeeAsyncActions, []);

    const { startConsultation } = useAIScribeStartConsultation({
        encounterId: props.model.get("EncounterId"),
        onAIScribeSubscriptionInactive: () => {
            sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribeSubscriptionExpiredPopup, {});
        },
        onAIScribeConsultationCreated: (consultationId, url, encounterId) => {
            sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribePopup, {
                aiScribeConsultationId: consultationId,
                aiScribeUrl: url,
                encounterId: encounterId,
                openBottomPos: props.model.get("AIScribePopupBottomPos"),
                openRightPos: props.model.get("AIScribePopupRightPos"),
                patientContactId: props.model.get("PatientContactId"),
                patientContactFacetId: props.model.get("PatientContactFacetId")
            });
        }
    });

    const { getText } = useTranslation();

    const [generatedNote, setGeneratedNote] = useState<AIScribeConsultationNoteModel>(null);
    const [openAIScribePopupWhenLinked, setOpenAIScribePopupWhenLinked] = useState(false);

    const [showCloseWarningPopup, setShowCloseWarningPopup] = useState(false);
    const [showApprovedMedicalNoteWarningPopup, setShowApprovedMedicalNoteWarningPopup] = useState(false);
    const [showDeletedMedicalNoteWarningPopup, setShowDeletedMedicalNoteWarningPopup] = useState(false);
    const [isCurrentEmailLoading, setIsCurrentEmailLoading] = useState<boolean>(false);
    const [cachedEmployeeEmail, setCachedEmployeeEmail] = useState<Readonly<cachedEmployeeEmail>>({
        address: null,
        savedDateInMilliseconds: null
    });

    const {
        isRecording,
        currentRecordingDuration,
        hasAIScribeContentChanged,
        startRecording,
        stopRecording,
        setErrorReason,
        resetAIScribeContentState
    } = useAIScribeContentState();

    const {
        unableToSaveBy,
        unableToSaveDate,
        setUnableToSaveMedicalNoteWarningPopupState,
        resetUnableToSaveMedicalNoteWarningPopupState
    } = useUnableToSaveMedicalNoteWarningPopupState();

    const sendAppendEncounterRequest = useCallback(async (
        patientContactId: string,
        aIScribeConsultationId: string,
        encounter: Readonly<Encounter>,
        forceOverride: boolean = false
    ) => await action.appendEnounterProgressNote(
        patientContactId,
        aIScribeConsultationId,
        encounter,
        forceOverride
    ), [action]);

    const sendCreateNewEncounterRequest = useCallback(async (
        patientContactFacetId: string,
        aIScribeConsultationId: string,
        encounter: Readonly<Encounter>
    ) => await action.createNewNote(
        patientContactFacetId,
        aIScribeConsultationId,
        encounter
    ), [action]);

    async function handleSyncComplete(generatedNote: AIScribeConsultationNoteModel): Promise<void> {
        setGeneratedNote(generatedNote);

        const patientContactId = props.model.get("PatientContactId");
        const aIScribeConsultationId = props.model.get("AIScribeConsultationId");
        const encounterId = props.model.get("EncounterId");

        const editingEncounterModel = getEncounterModelIfUserIsCurrentlyEditing(patientContactId, encounterId);
        if (editingEncounterModel) {
            const encounter = appendGeneratedNoteToMedicalNote(
                editingEncounterModel,
                generatedNote
            );
            await saveAppendEncounter(patientContactId, aIScribeConsultationId, encounter, true, false);
        }
        else {
            const encounter = appendGeneratedNoteToMedicalNote(
                {
                    Id: encounterId
                } as Encounter,
                generatedNote
            );
            await saveAppendEncounter(patientContactId, aIScribeConsultationId, encounter, false, true);
        }
    }

    async function saveAppendEncounter(
        patientContactId: string,
        aIScribeConsultationId: string,
        encounter: Readonly<Encounter>,
        forceOverride: boolean = false,
        showToast: boolean = false
    ): Promise<void> {
        const responseData = await sendAppendEncounterRequest(
            patientContactId,
            aIScribeConsultationId,
            encounter,
            forceOverride
        );

        if (responseData.DeletedBy && responseData.DeletionDateUtc) {
            setShowDeletedMedicalNoteWarningPopup(true);
            setUnableToSaveMedicalNoteWarningPopupState(responseData.DeletedBy, responseData.DeletionDateUtc);
        }
        else if (responseData.EncounterAlreadySignedOffUserContactFacetId && responseData.EncounterAlreadySignedOffDateTime) {
            setShowApprovedMedicalNoteWarningPopup(true);
            setUnableToSaveMedicalNoteWarningPopupState(responseData.EncounterAlreadySignedOffUserContactFacetId, responseData.EncounterAlreadySignedOffDateTime);
        }
        else {
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateProgressNote, {
                PatientContactId: patientContactId,
                EncounterId: responseData.EncounterId,
                VisitPurpose: responseData.VisitPurpose,
                HistoricalNote: responseData.HistoricalNote,
                ProgressNote: responseData.ProgressNote,
                ExamenNote: responseData.ExamenNote,
                RadiologyNote: responseData.RadiologyNote,
                LabNote: responseData.LabNote,
                OtherPlanNote: responseData.OtherPlanNote
            });

            showToast && displayToast(MessageToast.Success, getText("AIScribe_NoteGeneratedSuccessfully"));
            closeAIScribePopup();
        }
    }

    function closeAIScribePopup(): void {
        sessionStore.dispatch(LocalOperations.AIScribe.CloseAIScribePopup, {});
        resetAIScribeContentState();
        setGeneratedNote(null);
    }

    function closeAIScribeSubscriptionExpiredPopup(): void {
        sessionStore.dispatch(LocalOperations.AIScribe.CloseAIScribeSubscriptionExpiredPopup, {});
    }

    function closeAIScribeUnlinkCarewayPopup(unlinkUser: boolean = false): void {
        sessionStore.dispatch(LocalOperations.AIScribe.CloseAIScribeUnlinkCarewayPopup, { unlinkUser });
    }

    function showUnlinkCarewayPopup(): void {
        closeAIScribeSubscriptionExpiredPopup();
        sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribeUnlinkCarewayPopup, {});
    }

    function handleOnAIScribePopupClose(): void {
        if (hasAIScribeContentChanged()) {
            setShowCloseWarningPopup(true);
        }
        else {
            closeAIScribePopup();
        }
    }

    function handleOnWarningPopupClose(): void {
        setShowCloseWarningPopup(false);
        closeAIScribePopup();
    }

    async function handleOnCreateNewNoteClicked(): Promise<void> {
        setShowApprovedMedicalNoteWarningPopup(false);
        setShowDeletedMedicalNoteWarningPopup(false);
        resetUnableToSaveMedicalNoteWarningPopupState();

        const response = await sendCreateNewEncounterRequest(
            props.model.get("PatientContactFacetId"),
            props.model.get("AIScribeConsultationId"),
            appendGeneratedNoteToMedicalNote(
                {} as Encounter,
                generatedNote
            )
        );

        patientStore.ActionsMedicalNote.LoadEncounterForView(response.Encounter.Id, null, SubServerOperations.MedicalNote.ForSummaryRefresh);

        closeAIScribePopup();
        displayToast(MessageToast.Success, getText("AIScribe_NoteGeneratedSuccessfully"));
    }

    function isMarketingPopupOpen(): boolean {
        return cacheStore.ClinicSettings.EnableAIScribeAlwaysOn
            && props.model.get("ShowAIScribeMarketingPopup")
            && !props.model.get("AIScribeLinkedUser");
    }

    async function loadCurrentUserEmail(): Promise<string> {
        const { address, savedDateInMilliseconds } = cachedEmployeeEmail;

        const now = Date.now();
        const cacheDurationInMilliseconds = 5 * 60_000;    // 5 minutes in milliseconds

        if (address != null && (now - savedDateInMilliseconds) < cacheDurationInMilliseconds) {
            return address;
        }

        const employeeEmails = await employeeAction.getCurrentEmployeeEmails();
        const email = employeeEmails?.LastVerifiedEmail ?? employeeEmails?.Emails?.[0]?.Address ?? "";

        setCachedEmployeeEmail({ address: email, savedDateInMilliseconds: now });

        return email;
    }

    async function openRegisterPage(): Promise<void> {
        if (isCurrentEmailLoading) {
            return;
        }

        setIsCurrentEmailLoading(true);
        try {
            const url = new URL(cacheStore.ClinicSettingsServices.CoeurWayCreateAccountUrl);
            const currentEmployee = cacheStore.GetCurrentEmployee();
            const params = {
                lang: sessionStore.locale === Locale.fr ? "fr" : "en",
                ...(currentEmployee && {
                    firstname: currentEmployee.FirstName,
                    lastname: currentEmployee.LastName
                }),
                email: await loadCurrentUserEmail(),
                noskip: true
            } as const;

            Object.keys(params).forEach(key => {
                if (params[key]) {
                    url.searchParams.append(key, params[key]);
                }
            });

            window.open(url.toString(), "_blank", "noopener");
        }
        finally {
            setIsCurrentEmailLoading(false);
        }
    }

    function openLinkAccountPopup(): void {
        setOpenAIScribePopupWhenLinked(true);
        sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribeLinkPopup);
    }

    return (
        <div id="ai-scribe-container" data-cy="ai-scribe-container">
            {props.model.get("ShowAIScribePopup") && (
                <AIScribePopup
                    consultationId={props.model.get("AIScribeConsultationId")}
                    url={props.model.get("AIScribeConsultationUrl")}
                    openBottomPos={props.model.get("AIScribePopupBottomPos")}
                    openRightPos={props.model.get("AIScribePopupRightPos")}
                    isRecording={isRecording}
                    recordingDuration={currentRecordingDuration}
                    onNoteSyncCompleted={handleSyncComplete}
                    onRecordingStarted={() => startRecording()}
                    onRecordingStopped={(duration: number) => stopRecording(duration)}
                    onNoteSyncFailed={err => setErrorReason(err)}
                    onClose={handleOnAIScribePopupClose}
                />
            )}

            {props.model.get("ShowAIScribeLinkPopup") && (
                <AIScribeLinkPopup
                    email={props.model.get("AIScribeLinkedUser")?.getEmail()}
                    onClose={() => {
                        sessionStore.dispatch(LocalOperations.AIScribe.CloseAIScribeLinkPopup, {});
                        setOpenAIScribePopupWhenLinked(false);
                    }}
                    onLinked={async user => {
                        sessionStore.dispatch(LocalOperations.AIScribe.CloseAIScribeLinkPopup, { user });
                        if (openAIScribePopupWhenLinked) {
                            await startConsultation();
                            setOpenAIScribePopupWhenLinked(false);
                        }
                    }}
                    onCreateAccount={openRegisterPage}
                />
            )}

            {props.model.get("ShowSubscriptionExpiredPopup") && (
                <AIScribeSubscriptionExpiredPopup
                    onRenewClicked={() => closeAIScribeSubscriptionExpiredPopup()}
                    onClose={() => closeAIScribeSubscriptionExpiredPopup()}
                    onUnlinkCarewayAccount={showUnlinkCarewayPopup}
                />
            )}

            {props.model.get("ShowAIScribeUnlinkPopup") && (
                <AIScribeUnlinkCarewayPopup
                    onUnlinkCarewayAccount={() => closeAIScribeUnlinkCarewayPopup(true)}
                    onClose={() => closeAIScribeUnlinkCarewayPopup()}
                />
            )}

            {showCloseWarningPopup && (
                <AIScribeCloseWarningPopup
                    onCloseAIAssistant={handleOnWarningPopupClose}
                    onKeepOpen={() => setShowCloseWarningPopup(false)}
                    onClose={() => setShowCloseWarningPopup(false)}
                />
            )}

            {(showApprovedMedicalNoteWarningPopup && unableToSaveBy && unableToSaveDate) && (
                <AIScribeMedicalNoteApprovedPopup
                    approvedBy={unableToSaveBy}
                    approvedDate={unableToSaveDate}
                    onCreateNewNoteClicked={handleOnCreateNewNoteClicked}
                />
            )}

            {(showDeletedMedicalNoteWarningPopup && unableToSaveBy && unableToSaveDate) && (
                <AIScribeMedicalNoteDeletedPopup
                    deletedBy={unableToSaveBy}
                    deletionDateUTC={unableToSaveDate}
                    onCreateNewNoteClicked={handleOnCreateNewNoteClicked}
                />
            )}

            {aiScribeStore.Model.get("ShowAIScribeWarningActiveAssistant") && (
                <AIScribeWarningActiveAssistant
                    onOk={() => sessionStore.dispatch(LocalOperations.AIScribe.CloseAIScribeWarningActiveAssistant)}
                    onClose={() => sessionStore.dispatch(LocalOperations.AIScribe.CloseAIScribeWarningActiveAssistant)}
                />
            )}

            {isMarketingPopupOpen() && (
                <AIScribeMarketingPopup
                    onRemindMeLater={() => sessionStore.dispatch(LocalOperations.AIScribe.RemindMeLaterAIScribeMarketingPopup)}
                    onCreateAccount={() => {
                        openRegisterPage();
                        openLinkAccountPopup();
                    }}
                    onLinkAccount={() => openLinkAccountPopup()}
                    onClose={() => {
                        sessionStore.dispatch(LocalOperations.AIScribe.CloseAIScribeMarketingPopup);
                        setOpenAIScribePopupWhenLinked(false);
                    }}
                />
            )}
        </div>
    );
}

export { AIScribeContainer };
